import React from "react";
import { 
  Box, 
  Drawer, 
  List, 
  ListItemButton, 
  ListItemText, 
  ListItemIcon, 
  useTheme,
  Typography,
  Divider,
  Tooltip,
  Collapse,
  styled,
  IconButton
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupIcon from "@mui/icons-material/Group";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useLocation, useNavigate } from "react-router-dom";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import { useThemeContext } from "../context/ThemeContext";
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import MenuIcon from '@mui/icons-material/Menu';
import CategoryIcon from '@mui/icons-material/Category';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import SummarizeIcon from '@mui/icons-material/Summarize';

interface SidebarProps {
  isDrawerOpen: boolean;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isCollapsed: boolean;
  setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ 
  isDrawerOpen, 
  setDrawerOpen,
  isCollapsed,
  setIsCollapsed 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { isDarkMode } = useThemeContext();
  const [expandedItems, setExpandedItems] = React.useState<Record<string, boolean>>({});

  const handleToggle = (itemText: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemText]: !prev[itemText],
    }));
  };

  const menuItems: MenuItem[] = [
    { text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard/overview" },
    { 
      text: "Analytics", 
      icon: <AnalyticsIcon />,
      
      children: [
        { text: "Real-time", icon: <BubbleChartIcon />, path: "/dashboard/analytics/realtime" }, 

        { text: "Sales Reports", icon: <SummarizeIcon />, path: "/dashboard/analytics/sales-reports" },
      ]
    },
    { text: "Admins", icon: <AdminPanelSettingsIcon />, path: "/dashboard/admin-users" },
    { text: "Customers", icon: <GroupIcon />, path: "/dashboard/customers" },
    { 
      text: "Products", 
      icon: <InventoryIcon />,
      children: [
        { text: "All Products", icon: <InventoryIcon />, path: "/dashboard/products" },
        { text: "Categories", icon: <CategoryIcon />, path: "/dashboard/categories" },
        { text: "Brands", icon: <BrandingWatermarkIcon />, path: "/dashboard/brands" },
      ]
    },
    { text: "Orders", icon: <ShoppingCartIcon />, path: "/dashboard/orders" },
    { text: "Pricing", icon: <PriceChangeIcon />, path: "/dashboard/pricing-manager" },
  ];

  const drawerContent = (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        width: isCollapsed ? 72 : 240,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Header with collapse button */}
      <Box 
        sx={{ 
          px: 2,
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          mt: 6
        }}
      >
        {!isCollapsed && (
          <Typography variant="h6" noWrap>
            Admin Panel
          </Typography>
        )}
        <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
          <FastRewindIcon />
        </IconButton>
      </Box>

      {/* Menu Items */}
      <List sx={{ p: 1, flexGrow: 1 }}>
        {menuItems.map((item, index) => {
          const hasChildren = item.children && item.children.length > 0;
          const isExpanded = expandedItems[item.text] || false;
          const isActiveParent = hasChildren ? 
            item.children!.some(child => location.pathname === child.path) : 
            false;

          if (isCollapsed) {
            return (
              <Tooltip title={item.text} placement="right" key={index}>
                <ListItemButton
                  selected={hasChildren ? isActiveParent : location.pathname === item.path}
                  onClick={() => {
                    if (hasChildren) {
                      handleToggle(item.text);
                    } else {
                      navigate(item.path!);
                      setDrawerOpen(false);
                    }
                  }}
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                    justifyContent: 'center',
                    minHeight: 48,
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      color: (hasChildren ? isActiveParent : location.pathname === item.path) ? 
                        theme.palette.primary.main : 
                        theme.palette.text.secondary,
                      minWidth: 'auto',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            );
          }

          return (
            <React.Fragment key={index}>
              {hasChildren ? (
                <>
                  <ListItemButton
                    selected={isActiveParent}
                    onClick={() => handleToggle(item.text)}
                    sx={{
                      mb: 0.5,
                      borderRadius: 1,
                      "&.Mui-selected": {
                        backgroundColor: theme.palette.action.selected,
                      },
                    }}
                  >
                    <ListItemIcon>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children!.map((child, childIndex) => (
                        <ListItemButton
                          key={childIndex}
                          selected={location.pathname === child.path}
                          onClick={() => {
                            navigate(child.path!);
                            setDrawerOpen(false);
                          }}
                          sx={{
                            pl: 4,
                            mb: 0.5,
                            borderRadius: 1,
                            "&.Mui-selected": {
                              backgroundColor: theme.palette.action.selected,
                            },
                          }}
                        >
                          <ListItemIcon>
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText primary={child.text} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => {
                    navigate(item.path!);
                    setDrawerOpen(false);
                  }}
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                >
                  <ListItemIcon>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <Box
        component="nav"
        sx={{
          position: 'relative',
          zIndex: theme.zIndex.drawer,
          width: isCollapsed ? 72 : 240,
          flexShrink: 0,
          display: { xs: "none", md: "block" },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Drawer
          variant="permanent"
          open
          sx={{
            '& .MuiDrawer-paper': {
              position: 'absolute',
              boxSizing: 'border-box',
              width: isCollapsed ? 72 : 240,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              backgroundColor: 'transparent',
              borderRight: 'none',
              boxShadow: 'none',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 240,
            boxSizing: 'border-box',
            backgroundColor: theme.palette.background.default,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar;